import { useProjectParams } from '@hooks/useProjectParams';
import { getTableTreeRows } from 'components/TableLikeTree/utils';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { BimWorkOrderVersionObjectData } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { Button, IconBackward, Loader, Table, cnMixSpace } from 'uikit';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Style from '../../WorkOrder/BimWorkOrderStructure/BimWorkOrderStructure.module.css';
import { useGetBimWorkOrderDataFromProject } from '../../WorkOrder/hooks';

import {
    bimWorkOrderVersionConstructionTypesGql,
    bimWorkOrderVersionSeasonsGql,
} from './BimVersionViewStructureQueries';
import { useBimWorkOrderVersionSelectionList } from './hooks/useBimWorkOrderVersionSelectionList';
import { useTableTreeVersionColumns } from './LeftVersionStructureFilter/hooks/useTableTreeVersionColumns';
import { LeftVersionStructureFilter } from './LeftVersionStructureFilter/LeftVersionStructureFilter';

const getParentProp = (item: BimWorkOrderVersionObjectData) => (item.parentId ? String(item.parentId) : 'root');
const groupByParentId = R.groupBy(getParentProp);
const sortByParentBaseOrder = R.sortBy(R.prop('order'));

export interface ITableStructureItem {
    id: number;
    title: string;
    children?: ITableStructureItem[];
    treeLevel?: number;
    treeLevelShift?: number;
    parent?: ITableStructureItem;
    isFirstChild?: boolean;
    isLastChild?: boolean;
}

export const BimVersionViewStructure = () => {
    const { id: projectId, orderId, methodology, versionId } = useProjectParams();
    const { projectData, projectDataStatus } = useProjectData(projectId);
    const [rows, setRows] = useState<any[]>([]);
    const [shownIds, setShownIds] = useState<number[]>([]);
    const [constructionTypeId, setConstructionTypeId] = useState<number>();
    const navigate = useNavigate();
    const workOrderId = Number(orderId);
    const workOrderVersionId = Number(versionId);

    const itemsIds = useMemo(() => rows.map((r) => r?.id), [rows]);

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const handleBackVersion = useCallback(
        () => navigate(`/projects/${methodology}/${projectId}/order/${orderId}/version/${versionId}`),
        [methodology, navigate, orderId, projectId, versionId],
    );

    const variables = useMemo(
        () => ({
            workOrderVersionId,
        }),
        [workOrderVersionId],
    );

    const [versionSeasonsData, versionSeasonsStatus] = useRequest({
        requestId: 'bimWorkOrderVersionSeasons',
        query: bimWorkOrderVersionSeasonsGql,
        variables,
    });

    const [bimWorkOrderVersionConstTypesData, bimWorkOrderVersionConstTypesStatus] = useRequest({
        requestId: 'bimWorkOrderVersionConstructionTypesITableLikeTreeItem',
        query: bimWorkOrderVersionConstructionTypesGql,
        variables,
    });

    const {
        bimWorkOrderObjectSelectionListReset,
        bimWorkOrderObjectSelectionListData,
        bimWorkOrderObjectSelectionListStatus,
        bimWorkOrderObjectSelectionListMakeRequest,
    } = useBimWorkOrderVersionSelectionList(workOrderVersionId, false);

    const tableTreeItems: ITableStructureItem[] = useMemo(() => {
        if (bimWorkOrderObjectSelectionListData?.bimWorkOrderVersionObjects?.items?.length) {
            const groupedByParent = groupByParentId(
                bimWorkOrderObjectSelectionListData?.bimWorkOrderVersionObjects?.items as any[],
            );
            const mapper = (o: any): ITableStructureItem => ({
                id: o.id,
                title: o.name,
                children: sortByParentBaseOrder(groupedByParent[o.id] || [])?.map(mapper),
            });

            return sortByParentBaseOrder(groupedByParent.root || []).map(mapper);
        }

        return [];
    }, [bimWorkOrderObjectSelectionListData?.bimWorkOrderVersionObjects?.items]);

    useEffect(() => {
        bimWorkOrderObjectSelectionListReset(undefined);

        return () => {
            bimWorkOrderObjectSelectionListReset(undefined);
        };
    }, [bimWorkOrderObjectSelectionListReset]);

    useEffect(() => {
        if (workOrderVersionId) {
            bimWorkOrderObjectSelectionListMakeRequest(workOrderVersionId, constructionTypeId);
        }
    }, [bimWorkOrderObjectSelectionListMakeRequest, constructionTypeId, workOrderVersionId]);

    const isLoadingPage = useMemo(
        () =>
            projectDataStatus === RequestStatus.FETCHING ||
            versionSeasonsStatus === RequestStatus.FETCHING ||
            bimWorkOrderVersionConstTypesStatus === RequestStatus.FETCHING,
        [bimWorkOrderVersionConstTypesStatus, projectDataStatus, versionSeasonsStatus],
    );

    const isLoadTable = useMemo(
        () => bimWorkOrderObjectSelectionListStatus === RequestStatus.FETCHING,
        [bimWorkOrderObjectSelectionListStatus],
    );

    const dataTypes: any = bimWorkOrderVersionConstTypesData?.bimWorkOrderVersionConstructionTypes.data;

    const filterItems = useMemo(() => {
        if (dataTypes) {
            const dataTypesUpdate = dataTypes.map((item: any) => ({
                title: item.name,
                id: item.id,
            }));

            return dataTypesUpdate;
        }

        return [];
    }, [dataTypes]);

    useEffect(() => {
        if (dataTypes) {
            setConstructionTypeId(dataTypes[0].id);
        }
    }, [dataTypes]);

    const selectedSeasons = useMemo(() => {
        if (versionSeasonsData) {
            const { data } = versionSeasonsData.bimWorkOrderVersionSeasons;

            return `${data?.firstSeason.year} - ${data?.lastSeason.year}`;
        }

        return '';
    }, [versionSeasonsData]);

    const { workOrderTitle } = useGetBimWorkOrderDataFromProject({});

    const onLeftFilterRowSelected = useCallback((itemId: number) => {
        setConstructionTypeId(itemId);
    }, []);

    const hideItems = useCallback(
        (ids: number[]) => {
            setShownIds(shownIds.filter((id) => !ids.includes(id)));
        },
        [shownIds],
    );

    const showItems = useCallback(
        (ids: number[]) => {
            setShownIds([...shownIds, ...ids]);
        },
        [shownIds],
    );

    const switchExpandAll = useCallback(
        (expandAll: boolean) => {
            const newShownIds = expandAll ? itemsIds : tableTreeItems.map((item: ITableStructureItem) => item.id);

            setShownIds(newShownIds);
        },
        [itemsIds, tableTreeItems],
    );

    const columns = useTableTreeVersionColumns({
        shownIds,
        hideItems,
        showItems,
        switchExpandAll,
        rows,
        tableTreeItems,
    });

    useEffect(() => {
        const newRows = getTableTreeRows(tableTreeItems);

        setRows(newRows);
        const newShownIds: number[] = newRows.map((r) => r?.id);

        setShownIds(newShownIds);
    }, [tableTreeItems]);

    const rowsToShow = useMemo(() => rows.filter((row) => shownIds.includes(row?.id)), [rows, shownIds]);

    const getClassName = useCallback(
        (data) =>
            `${data?.row?.isMessageItem ? 'message-row ' : ''} treeLevel_${data?.row?.treeLevel} ${
                data?.row?.treeLevel > 2 ? 'treeLevelMoreThanSecond' : ''
            }`,
        [],
    );

    return (
        <ProjectPageLayout
            layoutEntityKey={workOrderId}
            title={`Cтруктура ${workOrderTitle}`}
            breadCrumbsItems={breadCrumbsItems}
        >
            <div className={Style.wrapperBackButton}>
                <Button
                    className={cnMixSpace({ mR: 'xs' })}
                    label="Вернуться"
                    view="primary"
                    size="s"
                    iconLeft={IconBackward}
                    onClick={handleBackVersion}
                />
            </div>
            <div className={Style.bimWorkOrderStructureContainer}>
                {isLoadingPage ? (
                    <div style={{ width: '100%' }}>
                        <Loader />
                    </div>
                ) : (
                    <>
                        <section style={{ width: '100%' }}>
                            <p className={Style.selectionSeasons}>
                                <span>Выбранные сезоны:</span>
                                {selectedSeasons}
                            </p>
                        </section>
                        <div className={Style.outerBordered}>
                            <LeftVersionStructureFilter
                                items={filterItems}
                                onRowSelected={onLeftFilterRowSelected}
                                selectedId={constructionTypeId}
                            />
                        </div>
                    </>
                )}
                <div className={Style.wrapperBorderedRightFilter}>
                    {isLoadTable ? (
                        <Loader />
                    ) : (
                        <Table
                            columns={columns}
                            rows={rowsToShow}
                            size="s"
                            stickyHeader
                            borderBetweenColumns
                            verticalAlign="center"
                            getAdditionalClassName={getClassName}
                            className="outer-bordered"
                        />
                    )}
                </div>
            </div>
        </ProjectPageLayout>
    );
};

BimVersionViewStructure.displayName = 'BimVersionViewStructure';
